/**
 * ChatPanel - Main chat container component
 * 
 * Self-contained chat panel that handles:
 * - WebSocket connection via useChat hook
 * - Message display and sending
 * - File attachments
 * - Collapsible mode
 */
import { useState, useCallback } from "react";
import { useChat, useCurrentUser } from "@crm/api";
import { ChatHeader } from "./ChatHeader";
import { MessageList } from "./MessageList";
import { MessageInput } from "./MessageInput";
import type { ChatPanelProps, ChatMessage } from "../types";

// Resolve relative URLs to absolute
function createUrlResolver(apiBaseUrl: string) {
  return function resolveUrl(url: string | null): string {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    const cleanBase = apiBaseUrl.replace(/\/+$/, "");
    const cleanUrl = url.replace(/^\/+/, "");
    return `${cleanBase}/${cleanUrl}`;
  };
}

export function ChatPanel({
  ticketId,
  isResolved = false,
  className = "",
  variant = "default",
  collapsible = false,
  defaultExpanded = true,
}: ChatPanelProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { data: currentUser } = useCurrentUser();

  // Get API base URL from environment
  const apiBaseUrl = typeof window !== "undefined" 
    ? (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL || "http://localhost:8000"
    : "http://localhost:8000";

  const resolveUrl = createUrlResolver(apiBaseUrl);

  const {
    messages,
    isLoading,
    hasMore,
    connectionState,
    sendMessage,
    loadMore,
  } = useChat(ticketId);

  // Transform messages to our type format
  const transformedMessages: ChatMessage[] = messages.map((msg) => ({
    id: msg.id,
    sender: msg.sender,
    content: msg.content,
    message_type: msg.message_type as ChatMessage["message_type"],
    attachment_url: msg.attachment_url,
    attachment_name: msg.attachment_name,
    attachment_size: msg.attachment_size,
    created_at: msg.created_at,
  }));

  const handleSend = useCallback((content: string) => {
    sendMessage(content);
  }, [sendMessage]);

  const handleAttach = useCallback((file: File) => {
    // TODO: Implement file upload via REST API
    console.log("File to upload:", file.name);
  }, []);

  const isDisabled = isResolved || connectionState !== "connected";
  const heightClass = variant === "compact" ? "h-[400px]" : "h-[600px]";

  // Collapsed state
  if (collapsible && !isExpanded) {
    const lastMessage = transformedMessages[transformedMessages.length - 1];
    return (
      <div
        className={`bg-card border border-border rounded-lg p-3 flex items-center justify-between cursor-pointer shadow-sm hover:shadow-md transition-all ${className}`}
        onClick={() => setIsExpanded(true)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold text-lg">
            💬
          </div>
          <div>
            <div className="text-sm font-semibold text-foreground">Chat</div>
            <div className="text-xs text-muted-foreground truncate max-w-[200px]">
              {lastMessage ? (lastMessage.content || "Attachment") : "No messages yet"}
            </div>
          </div>
        </div>
        <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${heightClass} bg-card border border-border rounded-lg overflow-hidden shadow-sm ${className}`}>
      <ChatHeader
        title="Chat"
        connectionState={connectionState}
        onMinimize={collapsible ? () => setIsExpanded(false) : undefined}
      />

      <MessageList
        messages={transformedMessages}
        currentUserId={currentUser?.id}
        isLoading={isLoading}
        hasMore={hasMore}
        onLoadMore={loadMore}
        resolveUrl={resolveUrl}
      />

      <MessageInput
        onSend={handleSend}
        onAttach={handleAttach}
        disabled={isDisabled}
        placeholder={isResolved ? "This ticket is resolved" : "Type a message..."}
      />
    </div>
  );
}
